import { ref } from 'vue';
import type { ToastVariants } from '@/components/ui/toaster';
import type { ToastAction, ToastState } from '@/types';

const currentToast = ref<ToastState | null>(null);
const isVisible = ref(false);
const timeoutId = ref<number | null>(null);

export const useToast = () => {
    /**
     * `action` puts a button on the toast ("Undo"). Pressing it runs the
     * handler and closes the toast; the toast stays up for `duration` like
     * any other, so an action is only offered while it is still sensible.
     */
    const show = (message: string, type: ToastVariants['variant'] = 'info', duration: number = 5000, action?: ToastAction) => {
        if (timeoutId.value !== null) {
            clearTimeout(timeoutId.value);
            timeoutId.value = null;
        }

        const toast: ToastState = {
            message,
            type,
            duration,
            timestamp: Date.now(),
            action,
        };

        currentToast.value = toast;
        isVisible.value = true;

        if (duration > 0) {
            timeoutId.value = setTimeout(() => {
                if (currentToast.value?.timestamp === toast.timestamp) {
                    hide();
                }
            }, duration);
        }
    };

    const hide = () => {
        if (timeoutId.value !== null) {
            clearTimeout(timeoutId.value);
            timeoutId.value = null;
        }

        isVisible.value = false;
        currentToast.value = null;
    };

    /** Runs the toast's action, then closes it — one press, never two. */
    const runAction = async () => {
        const action = currentToast.value?.action;

        if (action === undefined) {
            return;
        }

        hide();
        await action.handler();
    };

    return {
        currentToast,
        isVisible,
        show,
        hide,
        runAction,
    };
};
